import { useMemo, useState } from 'react'
import {
  CustomDrawer,
  GenericSuccess,
  DrawerError,
  NavTabs,
  Box,
} from '@yuno/design-system-web'
import { ConnectionForm } from '../connection-form'
import { ConstantText } from '../../utils/constants'
import { ConnectionDrawerFormProps } from '../../interfaces/connection.interfaces'
import { Category } from '../styles'
import ConnectionItem from '../connection-item'
import { ConnectionSetupInstructions } from '../connection-setup-instructions'
import styled from '@emotion/styled'
import { ConnectionSetupInstructionsDescription } from '../connection-setup-instructions/connection-setup-instructions-description'
import { ConnectionDrawerSuccess } from '../connection-drawer-success'
import ConnectionAccountsForm from '../connection-accounts-form/connection-account-form'
import { ConnectionInstructionsAlert } from '../connection-instructions-alert'
import { FormProvider } from '@yuno/dashboard-container-mfe'
import { ConnectionFormV2 } from '../connection_formv2'
import { useTranslation } from 'react-i18next'

const NavContainer = styled('div')`
  margin-top: -40px;
`

const DrawerBody = styled('div')`
  min-width: 360px;
`

const Head = styled(Box)`
  margin-top: 20px;
  margin-bottom: 40px;
`

const SuccessContainer = styled(Box)`
  flex: 1;
  display: flex;
  height: 80vh;
`

export const ConnectionDrawerForm = ({
  error,
  name,
  icon,
  description,
  fields,
  handleSubmit,
  handleSubmitConfirm,
  open,
  countries,
  providerId,
  onSecondaryAction,
  onPrimaryAction,
  onClose,
  status = 'idle',
  loading,
  paymentMethods,
  data,
  category,
  connectionResponse,
  connectionName,
  handleChangeCountry,
  setupInstructions,
  validating,
  validAccounts,
  postValidateAccountsSuccess,
  postValidateAccountsError,
  actualAccountName,
  handleClickReturn,
  instructions,
  testKeyValues,
  currentMode,
  providerResponse,
  integrationTypes,
  isAsync,
}: ConnectionDrawerFormProps) => {

  const { t , i18n} = useTranslation()
  const currentLanguage = i18n.language;
  const statusConst =
    error === ConstantText.ERROR_MESSAGE_NAME ? 'idle' : status
  const statusButton = useMemo(
    () => ({
      success: {
        label: t('done'),
        action: onClose.bind(null),
        secondaryAction: null,
      },
      error: {
        label: t('tryAgain'),
        action: () => onPrimaryAction(),
        secondaryAction: () => onSecondaryAction(),
      },
      idle: {
        label: connectionResponse ? t('saveChanges') : t('connectOne'),
        action: null,
        secondaryAction: () => onSecondaryAction(),
      },
      loading: {
        label: t('connecting'),
        action: null,
        secondaryAction: () => onSecondaryAction(),
      },
    }),
    [],
  )
  const [isValid, setIsValid] = useState(false)

  const handleIsValid = (valid: boolean) => {
    setIsValid(valid)
  }

  const [hasChangeValues, setHasChangeValues] = useState(false);
  const handleChange = (value: boolean) => {
    setHasChangeValues(value)
  }

  const tabs = [
    {
      label: t('credentials'),
      content: (
        <NavContainer>
          {instructions?.length > 0 && (
            <ConnectionInstructionsAlert instructions={instructions} />
          )}
          {providerResponse?.new_params_catalog?.length > 0 ? (
            <ConnectionFormV2
              connectionName={connectionName}
              connectionResponse={connectionResponse}
              onSubmit={handleSubmit}
              setFormValid={handleIsValid}
              testKeyValues={testKeyValues}
              currentMode={currentMode}
              paramsCatalog={providerResponse.new_params_catalog}
              providerId={providerId}
              handleHasChange={handleChange}
              isAsync={isAsync}
              status={status}
              error={error}
            />
          ) : (
            <ConnectionForm
              connectionName={connectionName}
              connectionResponse={connectionResponse}
              error={error}
              data={data}
              status={status}
              fields={fields}
              handleHasChange={handleChange}
              onSubmit={handleSubmit}
              countries={countries}
              paymentMethods={paymentMethods}
              isAsync={isAsync}
              setFormValid={handleIsValid}
              providerId={providerId}
              handleChangeCountry={handleChangeCountry}
              testKeyValues={testKeyValues}
              currentMode={currentMode}
              integrationTypes={integrationTypes}
            />
          )}
        </NavContainer>
      ),
    },
    {
      label: t('setupInstructions'),
      content: (
        <NavContainer>
          <ConnectionSetupInstructionsDescription providerName={name} />
          <ConnectionSetupInstructions setupInstructions={setupInstructions} />
        </NavContainer>
      ),
    },
  ]

  const returnChildren = () => {
    if (status === 'success') {
      return (
        <SuccessContainer>
          <ConnectionDrawerSuccess status={data?.status} name={name} />
        </SuccessContainer>
      )
    }

    if (validating) {
      return (
        <ConnectionAccountsForm
          onSubmit={handleSubmitConfirm}
          accounts={validAccounts}
          actualAccountName={actualAccountName}
        />
      )
    }

    if ((status === 'error' || postValidateAccountsError) && !error) {
      return <DrawerError title={t('connectionFailed')} description={t('oopsConnection')} transparent />
    }

    if (error === ConstantText.CONNECTION_ALREADY_EXISTS) {
      return (
        <DrawerError
          icon="XCircle"
          iconSize="75"
          title={t('connectionAttemptFailed')}
          description={t('oopsAccountCharacteristics')}
        />
      )
    }

    if (error && error !== ConstantText.ERROR_MESSAGE_NAME) {
      return <DrawerError title={t('connectionFailed')} description={t('oopsConnection')} transparent />
    }

    return (
      <>
        <Head>
          <ConnectionItem
            connection={{
              name,
              icon,
              description:
                description || connectionResponse?.provider_description,
              category,
              providerId,
            }}
            right={<Category>{category}</Category>}
            fullWidth
            isNewConnection
          />
        </Head>
        {setupInstructions?.length ? (
          <NavTabs tabs={tabs} />
        ) : (
          <NavTabs tabs={[tabs[0]]} />
        )}
      </>
    )
  }

  const primaryAction = () => {
    if (
      postValidateAccountsError ||
      (postValidateAccountsSuccess &&
        validAccounts?.length === 0 &&
        status !== 'success')
    ) {
      return onPrimaryAction()
    }
    if (status !== 'idle') {
      return statusButton[statusConst]?.action()
    }
    return null
  }

  const primaryType = () => {
    if (postValidateAccountsSuccess && validAccounts?.length === 0) {
      return 'button'
    }
    if (statusConst === 'idle' && !postValidateAccountsError) {
      return 'submit'
    }
    return 'button'
  }
  const primaryLabel = () => {
    if (validating) {
      return t('confirm')
    }
    if (postValidateAccountsError) {
      return t('tryAgain')
    }
    if (connectionResponse) {
      return t('saveChanges')
    }
    if (
      postValidateAccountsSuccess &&
      validAccounts?.length === 0 &&
      status !== 'success'
    ) {
      return t('tryAgain')
    }
    return statusButton[statusConst].label
  }

  const returnTitle = () =>
    validating
      ? t('applyMultipleAccounts')
      : connectionResponse
      ? currentLanguage.toLocaleLowerCase() === 'en' ? `${name} ${t('connectionTo')}` : `${t('connectionTo')}${name} `
      : `${t('connectTo')} ${name}`

  return (
    <CustomDrawer
      title={returnTitle()}
      onSecondaryAction={statusButton[statusConst]?.secondaryAction}
      onPrimaryAction={primaryAction.bind(null)}
      onClose={onClose}
      primaryType={primaryType()}
      formId="connection-form"
      primaryLabel={primaryLabel()}
      secondaryLabel={t('cancel')}
      anchor="right"
      loading={loading}
      open={open}
      isFormValid={isValid}
      handleClickReturn={handleClickReturn}
      showArrow={validating}
      hasChange={hasChangeValues}
    >
      <FormProvider>
        <DrawerBody>{returnChildren()}</DrawerBody>
      </FormProvider>
    </CustomDrawer>
  )
}
